import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Modal, Select, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

import { cloneSession, fetchReplayTargetKlasses, selectors } from 'state';

const { Paragraph } = Typography;

type Props = {
  session_id: number;
  visible: boolean;
  hide: () => void;
};

const CloneSessionModal: React.FC<Props> = ({ session_id, visible, hide }) => {
  const dispatch = useDispatch();
  const waiting = useSelector(selectors.waiting);
  const klassesWaiting = useSelector(selectors.replayTargetKlassesWaiting);
  const targetKlasses = useSelector(selectors.replayTargetKlasses);
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      dispatch(fetchReplayTargetKlasses({}));
      form.resetFields();
    }
  }, [dispatch, form, visible]);

  return (
    <Modal
      title={`Deep-clone finished session ${session_id}`}
      open={visible}
      onCancel={hide}
      footer={null}
      width={560}
      destroyOnClose
    >
      <Paragraph>
        <WarningOutlined style={{ marginRight: 8 }} />
        The source session is read-only. The clone is inserted into the selected
        existing class and uses its existing trainer and trainee accounts.
      </Paragraph>
      <Paragraph type="secondary">
        Only utilized source players are copied. Source priority is highest
        market value, then lowest, then remaining highest-to-lowest. Target
        trainees are assigned using a deterministic pseudorandom permutation.
        The clone runs in the background after it is queued.
      </Paragraph>

      <Form
        form={form}
        layout="vertical"
        onFinish={(data: { target_klass_id: number }) => {
          dispatch(
            cloneSession({
              session_id,
              target_klass_id: data.target_klass_id,
            }),
          );
          hide();
        }}
      >
        <Form.Item
          label="Existing target class"
          name="target_klass_id"
          rules={[{ required: true, message: 'Select a target class' }]}
        >
          <Select
            showSearch
            loading={klassesWaiting}
            optionFilterProp="label"
            placeholder="Select trainer / class"
            options={targetKlasses.map(klass => ({
              value: klass.id,
              label: `${klass.trainer_username} — ${klass.name} (#${klass.id})`,
            }))}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={hide} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button loading={waiting} type="primary" htmlType="submit">
            Queue deep clone
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CloneSessionModal;
